import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { EdnaWrapper } from '@providers/EdnaProvider';
import Text from '@components/Text/Text';
import { TYPOGRAPHY } from '@junglescout/styles-library';
import { TextInputProps } from './types';

const INPUT_HEIGHT = 36;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;
const InputWrapper = styled.div<Pick<TextInputProps, 'width'>>`
  height: ${INPUT_HEIGHT}px;
  position: relative;
  width: ${(props) => props.width};
`;
const StyledInput = styled.input<
  Pick<TextInputProps, 'error' | 'isLoadingAi' | 'width'>
>`
  ${TYPOGRAPHY.bodySm};
  position: relative;
  width: ${(props) => props.width};
  height: ${INPUT_HEIGHT}px;
  padding: 8px 12px;
  box-shadow: none !important;
  border-radius: 4px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  border: 1px solid
    ${(props) =>
      props.error ? props.theme.colors.red500 : props.theme.colors.grey100};
  border-radius: 4px;
  background-color: ${(props) => props.theme.colors.white};
  &::placeholder {
    color: ${(props) => props.theme.colors.grey500};
  }
  &:hover:not(:disabled) {
    border: 1px solid
      ${(props) =>
        props.error ? props.theme.colors.red600 : props.theme.colors.grey300};
  }
  &:disabled {
    cursor: not-allowed;
    background-color: ${(props) => props.theme.colors.grey050};
    color: ${(props) => props.theme.colors.grey500};
    &::placeholder {
      color: ${(props) => props.theme.colors.grey300};
    }
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0px 0px 4px 2px rgba(0, 0, 0, 0.1) inset !important;
    border-color: ${(props) => props.theme.colors.grey400} !important;
  }

  // Overrides for isLoadingAi
  ${(props) =>
    props.isLoadingAi &&
    css`
      border-color: transparent;
      height: ${INPUT_HEIGHT - 2}px;
      margin: 1px;
      width: calc(${props.width || '100%'} - 2px);
    `}
`;
const LoadingAiBorder = styled.div`
  --angle: 0deg;
  &::before,
  &::after {
    content: '';
    position: absolute;
    height: ${INPUT_HEIGHT}px;
    width: 100%;
    bottom: 0;
    left: 0;
    right: 0;
    border-radius: 5px;
    background-image: conic-gradient(
      from var(--angle),
      white 1%,
      #e80284 2%,
      #052cdb 100%
    );
    animation: spin 3s linear infinite;
  }
  &::after {
    filter: blur(1px);
    opacity: 0.5;
  }

  @property --angle {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: false;
  }
  @keyframes spin {
    0% {
      --angle: 0deg;
    }
    100% {
      --angle: 360deg;
    }
  }
`;

/**
 * TextInput component. This component is used to render a text input field, with optional label and error message. It accepts all the standard input props.
 */
const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      className,
      id,
      label,
      error,
      isLoadingAi = false,
      type = 'text',
      width = '100%',
      ...props
    },
    ref
  ) => {
    const showIsLoadingAi = isLoadingAi && !Boolean(error);

    return (
      <EdnaWrapper>
        <Container className={className}>
          {label && (
            <Text
              htmlFor={id}
              element="label"
              variant="bodySm"
              fontWeight="500"
              color="grey800"
            >
              {label}
            </Text>
          )}
          <InputWrapper width={width}>
            {showIsLoadingAi && (
              <LoadingAiBorder aria-label="input is loading indicator" />
            )}
            <StyledInput
              id={id}
              className={className}
              type={type}
              data-lpignore={!['email', 'password'].includes(type || 'text')}
              error={error}
              isLoadingAi={showIsLoadingAi}
              width={width}
              {...props}
              ref={ref}
            />
          </InputWrapper>
          {Boolean(error) && (
            <Text element="label" variant="bodySm" color="red500">
              {error}
            </Text>
          )}
        </Container>
      </EdnaWrapper>
    );
  }
);

TextInput.displayName = 'TextInput';

export default TextInput;
